import * as React from "react";
import * as ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";
import { ReactNode, RefObject } from "react";

export interface ModalProps {
	closeOnBackgroundClick?: boolean;
	onClosed?: () => void;
	children?: React.ReactNode;

	show?: boolean;
	noPortal?: boolean;
	overflow?: string;
}
let modalRoot = null;
export class Modal extends React.PureComponent<ModalProps> {
	handleBackgroundClick = () => {
		if (this.props.closeOnBackgroundClick) {
			Modal.hide();

			if (this.props.onClosed) {
				this.props.onClosed();
			}
		}
	};

	render() {
		let modalComponent = this.props.show ? (
			<div className="modal is-active" style={{ zIndex: 1000 }}>
				<div className="modal-background" onClick={this.handleBackgroundClick} />
				{this.props.children}
			</div>
		) : null;

		if (this.props.noPortal) {
			return modalComponent;
		}

		return ReactDOM.createPortal(modalComponent, Modal.getModalDOMElement());
	}

	private static getModalDOMElement(autocreate: boolean = true): HTMLElement {
		let modalContainer = document.getElementById("__modal");

		if (!modalContainer && autocreate) {
			modalContainer = document.createElement("div");
			modalContainer.id = "__modal";
			document.body.appendChild(modalContainer);
		}

		return modalContainer;
	}

	static hide() {
		let modalContainer = Modal.getModalDOMElement(false);

		if (modalContainer && modalRoot) {
			modalRoot.unmount(modalContainer);
			modalContainer.parentElement.removeChild(modalContainer);
		}
	}

	private static _showComplex(title: string, body: ReactNode, footer: ReactNode, props?: ModalProps) {
		Modal.show(
			<div
				className="modal-card"
				style={{ overflow: props && props.overflow ? props.overflow : "hidden", minWidth: 420, width: "auto" }}
			>
				<header className="modal-card-head">{title ? <p className="modal-card-title">{title}</p> : null}</header>
				<section className="modal-card-body" style={{ overflow: props && props.overflow ? props.overflow : "auto" }}>
					{body}
				</section>
				<footer className="modal-card-foot">{footer}</footer>
			</div>,
			props
		);
	}

	static show(children: ReactNode, props?: ModalProps) {
		Modal.hide();

		let modalComponent = (
			<Modal {...props} show={true} noPortal={true}>
				{children}
			</Modal>
		);
		let modalContainer = Modal.getModalDOMElement(true);
		modalRoot = createRoot(modalContainer);
		modalRoot.render(modalComponent);
	}

	static async alert(message: ReactNode, title?: string) {
		return new Promise<void>((resolve) => {
			Modal._showComplex(
				title,
				message,
				<button
					className="button is-primary"
					onClick={() => {
						Modal.hide();
						resolve();
					}}
				>
					OK
				</button>
			);
		});
	}

	static async popup(body: ReactNode, title?: string, closeOnBackgroundClick: boolean = true) {
		return new Promise<void>((resolve) => {
			Modal._showComplex(title, body, null, {
				closeOnBackgroundClick: closeOnBackgroundClick,
				onClosed: () => resolve()
			});
		});
	}

	static async prompt(
		message: ReactNode,
		title: string = "",
		defaultValue: string = "",
		confirmButtonTitle: string = "OK",
		cancelButtonTitle: string = "Cancel"
	): Promise<any> {
		let inputRef: RefObject<HTMLInputElement> = React.createRef();

		return new Promise<any>((resolve) => {
			Modal._showComplex(
				title,
				<div className="field">
					<label className="label">{message}</label>
					<div className="control">
						<input className="input" type="text" ref={inputRef} autoFocus defaultValue={defaultValue} />
					</div>
				</div>,
				<React.Fragment>
					{!confirmButtonTitle ? null : (
						<button
							className="button is-success"
							onClick={() => {
								let value = inputRef.current.value;
								Modal.hide();
								resolve(value);
							}}
						>
							{confirmButtonTitle}
						</button>
					)}
					{!cancelButtonTitle ? null : (
						<button
							className="button"
							onClick={() => {
								Modal.hide();
								resolve(false);
							}}
						>
							{cancelButtonTitle}
						</button>
					)}
				</React.Fragment>
			);
		});
	}

	static async confirm(
		message: ReactNode,
		title: string = "",
		confirmButtonTitle: string = "OK",
		cancelButtonTitle: string = "Cancel",
		extraFooter: any = null,
		overflow?: string
	): Promise<any> {
		return new Promise<any>((resolve) => {
			Modal._showComplex(
				title,
				message,
				<React.Fragment>
					{!confirmButtonTitle ? null : (
						<button
							className="button is-success"
							onClick={() => {
								resolve(true);
								Modal.hide();
							}}
						>
							{confirmButtonTitle}
						</button>
					)}
					{!cancelButtonTitle ? null : (
						<button
							className="button"
							onClick={() => {
								resolve(false);
								Modal.hide();
							}}
						>
							{cancelButtonTitle}
						</button>
					)}
					{extraFooter}
				</React.Fragment>,
				{ overflow: overflow }
			);
		});
	}
}
